import {
  useState,
  useCallback,
  useId,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Plus,
  Trash2,
  UsersRound,
  MapPin,
  Shuffle,
  Trophy,
  Sparkles,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  CheckSquare,
  Square,
  ImageIcon,
} from 'lucide-react';
import {
  recognizeTextFromImage,
  parseLinesFromOcrText,
  filterLikelyNames,
} from '../../lib/ocr';
import { extractNamesFromLinesWithLLM } from '../../lib/nameExtractLLM';

/** 性別 */
export type Gender = 'M' | 'F';

/** 參加人員：姓名、等級 1~5、性別 */
export interface Participant {
  id: string;
  name: string;
  level: number;
  gender?: Gender;
}

const GENDER_LABEL: Record<Gender, string> = { M: '男', F: '女' };

/** 場地：可編號或自訂名稱 */
export interface Court {
  id: string;
  label: string;
}

/** 編隊方式 */
export type FormationMode = 'random' | 'level-balance';

/** 一組對戰：場地、兩隊各兩人 */
export interface Match {
  courtLabel: string;
  teamA: [Participant, Participant];
  teamB: [Participant, Participant];
}

/** 換場編排：自動編排 | 指定編排 */
export type RotateMode = 'auto' | 'specify';

const DEFAULT_COURT_PREFIX = '場地';
const TEST_PARTICIPANT_COUNT = 30;
/** 與 member-frontend 活動交付共用：從活動導入的參加人員會寫入此 key（同源時使用） */
const BADMINTON_IMPORT_STORAGE_KEY = 'badminton-import-participants';

/** 從 URL ?import= 的 base64url 解出名單（跨域時由 member-frontend 帶入） */
function parseImportFromUrl(): { name: string }[] | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('import');
  if (!encoded) return null;
  try {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
    const json = decodeURIComponent(escape(atob(padded)));
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (item): item is { name: string } =>
        item != null && typeof (item as { name?: unknown }).name === 'string'
    );
  } catch {
    return null;
  }
}

/**
 * 羽球自動編隊對戰工具：參加人員（可增減、等級 1~5）、場地（可增減、可編號）、編隊方式，產生雙打對戰表
 */
export function BadmintonTeamTool() {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<
    Set<string>
  >(() => new Set());
  const [participantListExpanded, setParticipantListExpanded] = useState(true);
  const [courtListExpanded, setCourtListExpanded] = useState(true);
  const [courts, setCourts] = useState<Court[]>([]);
  const [formationMode, setFormationMode] =
    useState<FormationMode>('level-balance');
  const [matches, setMatches] = useState<Match[] | null>(null);
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [rotateTargetMatchIndex, setRotateTargetMatchIndex] = useState<
    number | null
  >(null);
  const [defaultGender, setDefaultGender] = useState<Gender>('M');
  const idSeed = useId();
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrStatus, setOcrStatus] = useState<string | null>(null);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [ocrMessage, setOcrMessage] = useState<string | null>(null);
  const [ocrRawText, setOcrRawText] = useState<string | null>(null);
  const ocrInputRef = useRef<HTMLInputElement>(null);

  /** 新增人員時自動勾選；刪除時從勾選移除 */
  useEffect(() => {
    setSelectedParticipantIds(prev => {
      const next = new Set(prev);
      participants.forEach(p => next.add(p.id));
      const ids = new Set(participants.map(p => p.id));
      next.forEach(id => {
        if (!ids.has(id)) next.delete(id);
      });
      return next;
    });
  }, [participants]);

  /** 啟動時若從活動交付「導入編隊工具」帶入參加人員：優先從 URL ?import=（跨域），否則從 localStorage（同源） */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const urlList = parseImportFromUrl();
    if (urlList && urlList.length > 0) {
      const now = Date.now();
      const list: Participant[] = urlList.map((item, i) => ({
        id: `import-${now}-${i}`,
        name:
          typeof item?.name === 'string' && item.name.trim()
            ? item.name.trim()
            : `人員${i + 1}`,
        level: 3,
        gender: 'M' as Gender,
      }));
      setParticipants(list);
      window.history.replaceState(
        {},
        '',
        window.location.pathname + window.location.hash
      );
      return;
    }
    try {
      const raw = window.localStorage.getItem(BADMINTON_IMPORT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed) || parsed.length === 0) {
        window.localStorage.removeItem(BADMINTON_IMPORT_STORAGE_KEY);
        return;
      }
      const now = Date.now();
      const list: Participant[] = parsed.map(
        (item: { name?: string }, i: number) => ({
          id: `import-${now}-${i}`,
          name:
            typeof item?.name === 'string' && item.name.trim()
              ? item.name.trim()
              : `人員${i + 1}`,
          level: 3,
          gender: 'M' as Gender,
        })
      );
      setParticipants(list);
      window.localStorage.removeItem(BADMINTON_IMPORT_STORAGE_KEY);
    } catch {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(BADMINTON_IMPORT_STORAGE_KEY);
      }
    }
  }, []);

  const addParticipant = useCallback(() => {
    const n = participants.length + 1;
    setParticipants(prev => [
      ...prev,
      {
        id: `${idSeed}-p-${n}-${Date.now()}`,
        name: `人員${n}`,
        level: 3,
        gender: defaultGender,
      },
    ]);
  }, [participants.length, idSeed, defaultGender]);

  /** 一鍵產生 30 個測試人員（姓名 測試1～測試30，等級隨機 1～5，性別用預設） */
  const generateTestParticipants = useCallback(() => {
    const now = Date.now();
    const list: Participant[] = Array.from(
      { length: TEST_PARTICIPANT_COUNT },
      (_, i) => ({
        id: `${idSeed}-p-test-${i}-${now}`,
        name: `測試${i + 1}`,
        level: Math.floor(Math.random() * 5) + 1,
        gender: defaultGender,
      })
    );
    setParticipants(list);
    setMatches(null);
  }, [idSeed, defaultGender]);

  /** 從圖片 OCR 辨識後，以邊緣端小模型（SmolLM2-360M）判斷人名並追加為參加人員（等級 3、預設性別） */
  const addParticipantsFromOcrImage = useCallback(
    async (file: File) => {
      setOcrError(null);
      setOcrMessage(null);
      setOcrStatus('OCR 辨識中…');
      setOcrLoading(true);
      try {
        const text = await recognizeTextFromImage(file);
        setOcrRawText(text);
        const lines = parseLinesFromOcrText(text);
        if (lines.length === 0) {
          setOcrError('無法從圖片辨識出文字，請確認圖片清晰且為中英文。');
          return;
        }
        const ruleCandidates = filterLikelyNames(lines);
        setOcrStatus('小模型判斷人名中…');
        let names = await extractNamesFromLinesWithLLM(lines, progress => {
          if (progress.text) {
            setOcrStatus(`模型載入中… ${Math.round(progress.progress * 100)}%`);
          }
        });
        setOcrStatus(null);
        let usedRuleFallback = false;
        // 若規則可抓出較多且較合理的人名，就優先採用規則結果
        if (
          ruleCandidates.length >= 4 &&
          ruleCandidates.length >= names.length
        ) {
          names = ruleCandidates;
          usedRuleFallback = true;
        }
        // 去重並限制最大人數（避免 OCR 多次重複名單）
        names = Array.from(new Set(names));
        const MAX_PARTICIPANTS = 24;
        if (names.length > MAX_PARTICIPANTS) {
          names = names.slice(0, MAX_PARTICIPANTS);
        }

        if (names.length === 0) {
          setOcrError(
            `辨識出 ${lines.length} 行，但無法判斷人名。請確認圖片為一行一人的名單，或改用手動新增。`
          );
          return;
        }
        const now = Date.now();
        const newList: Participant[] = names.map((name, i) => ({
          id: `${idSeed}-ocr-${now}-${i}`,
          name: name || `人員${participants.length + i + 1}`,
          level: 3,
          gender: defaultGender,
        }));
        setParticipants(prev => [...prev, ...newList]);
        setMatches(null);
        if (usedRuleFallback) {
          setOcrMessage(
            `小模型辨識結果較少，已改用規則篩選並加入 ${names.length} 筆。可手動刪除非人名的項目。`
          );
        } else if (lines.length > names.length) {
          setOcrMessage(
            `已用邊緣端小模型篩出 ${names.length} 筆人名並加入（共辨識 ${lines.length} 行）。`
          );
        }
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : '處理失敗，請稍後再試。';
        setOcrError(
          msg.includes('WebGPU') || msg.includes('webgpu')
            ? '此瀏覽器不支援 WebGPU，無法使用小模型。請改用 Chrome/Edge 或改用手動新增。'
            : msg
        );
      } finally {
        setOcrStatus(null);
        setOcrLoading(false);
        if (ocrInputRef.current) ocrInputRef.current.value = '';
      }
    },
    [idSeed, defaultGender, participants.length]
  );

  const toggleParticipantSelected = useCallback((id: string) => {
    setSelectedParticipantIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setMatches(null);
  }, []);

  const removeParticipant = useCallback((id: string) => {
    setParticipants(prev => prev.filter(p => p.id !== id));
    setMatches(null);
  }, []);

  const updateParticipant = useCallback(
    (
      id: string,
      patch: Partial<Pick<Participant, 'name' | 'level' | 'gender'>>
    ) => {
      setParticipants(prev =>
        prev.map(p => (p.id === id ? { ...p, ...patch } : p))
      );
      setMatches(null);
    },
    []
  );

  /** 點擊切換性別：男 ↔ 女 */
  const cycleParticipantGender = useCallback(
    (id: string) => {
      setParticipants(prev =>
        prev.map(p => {
          if (p.id !== id) return p;
          const next: Gender = (p.gender ?? defaultGender) === 'M' ? 'F' : 'M';
          return { ...p, gender: next };
        })
      );
      setMatches(null);
    },
    [defaultGender]
  );

  const addCourt = useCallback(() => {
    const n = courts.length + 1;
    setCourts(prev => [
      ...prev,
      {
        id: `${idSeed}-c-${n}-${Date.now()}`,
        label: `${DEFAULT_COURT_PREFIX}${n}`,
      },
    ]);
  }, [courts.length, idSeed]);

  const removeCourt = useCallback((id: string) => {
    setCourts(prev => prev.filter(c => c.id !== id));
    setMatches(null);
  }, []);

  const updateCourtLabel = useCallback((id: string, label: string) => {
    setCourts(prev =>
      prev.map(c =>
        c.id === id ? { ...c, label: label.trim() || c.label } : c
      )
    );
    setMatches(null);
  }, []);

  /** 等級平衡：將人員分成兩隊，使兩隊總等級差最小（貪心：依等級降序逐一放入總和較小的一隊） */
  function splitByLevelBalance(
    people: Participant[]
  ): [Participant[], Participant[]] {
    const sorted = [...people].sort((a, b) => b.level - a.level);
    const teamA: Participant[] = [];
    const teamB: Participant[] = [];
    let sumA = 0;
    let sumB = 0;
    for (const p of sorted) {
      if (sumA <= sumB) {
        teamA.push(p);
        sumA += p.level;
      } else {
        teamB.push(p);
        sumB += p.level;
      }
    }
    return [teamA, teamB];
  }

  /** 隨機分成兩隊 */
  function splitRandom(people: Participant[]): [Participant[], Participant[]] {
    const shuffled = [...people].sort(() => Math.random() - 0.5);
    const mid = Math.ceil(shuffled.length / 2);
    return [shuffled.slice(0, mid), shuffled.slice(mid)];
  }

  const selectedParticipants = participants.filter(p =>
    selectedParticipantIds.has(p.id)
  );

  const generateMatches = useCallback(() => {
    const selected = participants.filter(p => selectedParticipantIds.has(p.id));
    if (selected.length < 4) {
      setMatches(null);
      return;
    }

    const [teamA, teamB] =
      formationMode === 'level-balance'
        ? splitByLevelBalance(selected)
        : splitRandom(selected);

    const list: Match[] = [];
    const maxPairs = Math.min(
      Math.floor(teamA.length / 2),
      Math.floor(teamB.length / 2),
      courts.length
    );

    for (let i = 0; i < maxPairs; i++) {
      const a1 = teamA[i * 2];
      const a2 = teamA[i * 2 + 1];
      const b1 = teamB[i * 2];
      const b2 = teamB[i * 2 + 1];
      if (a1 && a2 && b1 && b2 && courts[i]) {
        list.push({
          courtLabel: courts[i].label,
          teamA: [a1, a2],
          teamB: [b1, b2],
        });
      }
    }
    setMatches(list);
  }, [participants, selectedParticipantIds, courts, formationMode]);

  const canGenerate = selectedParticipants.length >= 4 && courts.length >= 1;

  /** 每人已上場次數（依目前對戰表統計） */
  const participantPlayCount = useMemo(() => {
    const count = new Map<string, number>();
    participants.forEach(p => count.set(p.id, 0));
    if (matches) {
      for (const m of matches) {
        for (const p of [m.teamA[0], m.teamA[1], m.teamB[0], m.teamB[1]]) {
          count.set(p.id, (count.get(p.id) ?? 0) + 1);
        }
      }
    }
    return count;
  }, [participants, matches]);

  /** 取得該場對戰的 4 人 id 集合 */
  const getMatchPlayerIds = useCallback((m: Match) => {
    const set = new Set<string>();
    m.teamA.forEach(p => set.add(p.id));
    m.teamB.forEach(p => set.add(p.id));
    return set;
  }, []);

  /** 不在任一場的人員（可換上場；確保一人不同時在兩場） */
  const getParticipantsNotInAnyMatch = useCallback(() => {
    const idsInAnyMatch = new Set<string>();
    if (matches) {
      for (const m of matches) {
        getMatchPlayerIds(m).forEach(id => idsInAnyMatch.add(id));
      }
    }
    return participants.filter(p => !idsInAnyMatch.has(p.id));
  }, [participants, matches, getMatchPlayerIds]);

  /** 單一替換：在 match 中將 outId 換成 inP */
  const replaceInMatch = useCallback(
    (match: Match, outParticipantId: string, inP: Participant): Match => {
      const slot = (p: Participant) =>
        p.id === outParticipantId ? { ...inP } : p;
      return {
        courtLabel: match.courtLabel,
        teamA: [slot(match.teamA[0]), slot(match.teamA[1])],
        teamB: [slot(match.teamB[0]), slot(match.teamB[1])],
      };
    },
    []
  );

  /** 執行換場並關閉對話框；指定編排時傳入 1～4 位指定人員，不足由系統補齊；僅能選「未在任何場」的人，確保一人不同時在兩場 */
  const applyRotate = useCallback(
    (mode: RotateMode, specifySelected: Participant[] | null) => {
      if (rotateTargetMatchIndex === null || !matches) return;
      const m = matches[rotateTargetMatchIndex];
      const outside = getParticipantsNotInAnyMatch();

      let nextMatch: Match = m;
      if (
        mode === 'specify' &&
        specifySelected &&
        specifySelected.length >= 1
      ) {
        const selectedIds = new Set(specifySelected.map(p => p.id));
        const rest = outside.filter(p => !selectedIds.has(p.id));
        const pool = [...specifySelected];
        const need = 4 - pool.length;
        // 補齊時也依上場次數少者優先
        const restWithCount = rest.map(p => ({
          p,
          count: participantPlayCount.get(p.id) ?? 0,
          rnd: Math.random(),
        }));
        restWithCount.sort((a, b) =>
          a.count !== b.count ? a.count - b.count : a.rnd - b.rnd
        );
        for (let i = 0; i < need && i < restWithCount.length; i++) {
          pool.push(restWithCount[i].p);
        }
        if (pool.length === 4) {
          nextMatch = {
            courtLabel: m.courtLabel,
            teamA: [pool[0], pool[1]],
            teamB: [pool[2], pool[3]],
          };
        }
      } else if (mode === 'auto' && outside.length >= 4) {
        // 上場次數少者優先，同次數時隨機，避免有人一直排不到
        const withCount = outside.map(p => ({
          p,
          count: participantPlayCount.get(p.id) ?? 0,
          rnd: Math.random(),
        }));
        withCount.sort((a, b) =>
          a.count !== b.count ? a.count - b.count : a.rnd - b.rnd
        );
        const chosen = withCount.slice(0, 4).map(x => x.p);
        const shuffled = [...chosen].sort(() => Math.random() - 0.5);
        nextMatch = {
          courtLabel: m.courtLabel,
          teamA: [shuffled[0], shuffled[1]],
          teamB: [shuffled[2], shuffled[3]],
        };
      }

      setMatches(prev =>
        prev
          ? prev.map((match, i) =>
              i === rotateTargetMatchIndex ? nextMatch : match
            )
          : null
      );
      setRotateDialogOpen(false);
      setRotateTargetMatchIndex(null);
    },
    [
      rotateTargetMatchIndex,
      matches,
      getParticipantsNotInAnyMatch,
      replaceInMatch,
      participantPlayCount,
    ]
  );

  return (
    <>
      {/* 編隊方式 + 產生（對戰表上方） */}
      <section className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <Shuffle className="h-5 w-5 text-primary" />
          編隊方式
        </h2>
        <div className="mb-4 flex flex-wrap gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="formation"
              checked={formationMode === 'level-balance'}
              onChange={() => setFormationMode('level-balance')}
              className="text-primary"
            />
            <span className="text-gray-700 dark:text-gray-300">等級平衡</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              （兩隊總等級盡量接近）
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="formation"
              checked={formationMode === 'random'}
              onChange={() => setFormationMode('random')}
              className="text-primary"
            />
            <span className="text-gray-700 dark:text-gray-300">隨機</span>
          </label>
        </div>
        <button
          type="button"
          onClick={generateMatches}
          disabled={!canGenerate}
          className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          <Trophy className="h-4 w-4" />
          產生對戰表
        </button>
        {participants.length >= 4 &&
          participants.length > 4 * courts.length && (
            <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
              目前人數超過可排場數（{courts.length} 場 × 4
              人），請增加場地或減少人員。
            </p>
          )}
      </section>

      {/* 對戰表 */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
            對戰表
          </h2>
          {matches && matches.length > 0 ? (
            <ul className="space-y-4">
              {matches.map((m, idx) => (
                <li
                  key={idx}
                  className="rounded border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-gray-700/50"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-primary">
                      {m.courtLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setRotateTargetMatchIndex(idx);
                        setRotateDialogOpen(true);
                      }}
                      className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                    >
                      <RefreshCw className="h-4 w-4" />
                      換場
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-gray-900 dark:text-gray-100">
                    <span>
                      {m.teamA[0].name}({m.teamA[0].level}) + {m.teamA[1].name}(
                      {m.teamA[1].level})
                    </span>
                    <span className="text-gray-500">vs</span>
                    <span>
                      {m.teamB[0].name}({m.teamB[0].level}) + {m.teamB[1].name}(
                      {m.teamB[1].level})
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : canGenerate ? (
            <p className="text-gray-500 dark:text-gray-400">
              設定好人數與場地後，點擊「產生對戰表」。
            </p>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">
              請至少新增 4 位人員與 1 個場地，且總人數不超過 4 × 場地數。
            </p>
          )}
        </div>
      </div>

      {/* 參加人員（可收合、可勾選） */}
      <section className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <button
          type="button"
          onClick={() => setParticipantListExpanded(v => !v)}
          className="mb-3 flex w-full items-center gap-2 text-left text-lg font-semibold text-gray-900 dark:text-gray-100"
          aria-expanded={participantListExpanded}
        >
          {participantListExpanded ? (
            <ChevronDown className="h-5 w-5 text-primary" />
          ) : (
            <ChevronRight className="h-5 w-5 text-primary" />
          )}
          <UsersRound className="h-5 w-5 text-primary" />
          參加人員
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
            （已勾選 {selectedParticipantIds.size} / {participants.length}）
          </span>
        </button>
        {participantListExpanded && (
          <>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              可增減人員，等級 1～5（5 最強）。勾選即參與編隊，雙打每場 4 人。
            </p>
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                預設性別（新增時套用）
              </span>
              <button
                type="button"
                onClick={() => setDefaultGender('M')}
                className={`rounded-md px-2.5 py-1 text-sm font-medium ${
                  defaultGender === 'M'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                }`}
              >
                男
              </button>
              <button
                type="button"
                onClick={() => setDefaultGender('F')}
                className={`rounded-md px-2.5 py-1 text-sm font-medium ${
                  defaultGender === 'F'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                }`}
              >
                女
              </button>
            </div>
            <ul className="mb-3 space-y-3">
              {participants.map(p => (
                <li
                  key={p.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700/50"
                >
                  {/* 第一行：勾選、姓名、刪除 */}
                  <div className="mb-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleParticipantSelected(p.id)}
                      className="shrink-0 rounded p-0.5 text-gray-600 hover:text-primary dark:text-gray-400 dark:hover:text-primary"
                      aria-label={
                        selectedParticipantIds.has(p.id) ? '取消勾選' : '勾選'
                      }
                    >
                      {selectedParticipantIds.has(p.id) ? (
                        <CheckSquare className="h-5 w-5 text-primary" />
                      ) : (
                        <Square className="h-5 w-5" />
                      )}
                    </button>
                    <input
                      type="text"
                      value={p.name}
                      onChange={e =>
                        updateParticipant(p.id, { name: e.target.value })
                      }
                      placeholder="姓名"
                      className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    />
                    <button
                      type="button"
                      onClick={() => removeParticipant(p.id)}
                      className="shrink-0 rounded p-2 text-gray-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      aria-label="移除"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {/* 第二行：性別、等級、已上場次數 */}
                  <div className="flex flex-wrap items-center gap-3 pl-7 text-sm">
                    <button
                      type="button"
                      onClick={() => cycleParticipantGender(p.id)}
                      className={`rounded-md px-2.5 py-1 font-medium ring-1 transition-colors hover:opacity-90 ${
                        (p.gender ?? defaultGender) === 'M'
                          ? 'bg-blue-100 text-blue-800 ring-blue-300 dark:bg-blue-900/50 dark:text-blue-200 dark:ring-blue-700'
                          : 'bg-rose-100 text-rose-800 ring-rose-300 dark:bg-rose-900/50 dark:text-rose-200 dark:ring-rose-700'
                      }`}
                      title="點擊切換性別"
                    >
                      {GENDER_LABEL[p.gender ?? defaultGender]}
                    </button>
                    <label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      等級
                      <select
                        value={p.level}
                        onChange={e =>
                          updateParticipant(p.id, {
                            level: Number(e.target.value) as number,
                          })
                        }
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      >
                        {[1, 2, 3, 4, 5].map(l => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </label>
                    <span className="text-gray-500 dark:text-gray-400">
                      已上場 {participantPlayCount.get(p.id) ?? 0} 次
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            {ocrRawText !== null && (
              <details className="mb-2 rounded border border-dashed border-gray-300 bg-gray-50 p-2 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
                <summary className="cursor-pointer select-none text-gray-600 dark:text-gray-300">
                  檢視 OCR 原始文字（除錯用）
                </summary>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all">
                  {ocrRawText || '(無任何文字)'}
                </pre>
              </details>
            )}
            {ocrStatus && (
              <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                {ocrStatus}
              </p>
            )}
            {ocrError && (
              <p
                className="mb-2 text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {ocrError}
              </p>
            )}
            {ocrRawText && (
              <details className="mb-2 rounded border border-dashed border-gray-300 bg-gray-50 p-2 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
                <summary className="cursor-pointer select-none text-gray-600 dark:text-gray-300">
                  檢視 OCR 原始文字（除錯用）
                </summary>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all">
                  {ocrRawText}
                </pre>
              </details>
            )}

            {ocrMessage && (
              <p className="mb-2 text-sm text-green-700 dark:text-green-400">
                {ocrMessage}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <input
                ref={ocrInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                aria-hidden
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) addParticipantsFromOcrImage(file);
                }}
              />
              <button
                type="button"
                onClick={addParticipant}
                className="inline-flex items-center gap-2 rounded border border-dashed border-gray-400 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10 dark:hover:text-primary"
              >
                <Plus className="h-4 w-4" />
                新增人員
              </button>
              <button
                type="button"
                onClick={() => ocrInputRef.current?.click()}
                disabled={ocrLoading}
                className="inline-flex items-center gap-2 rounded border border-dashed border-gray-400 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:border-primary hover:bg-primary/5 hover:text-primary disabled:opacity-50 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10 dark:hover:text-primary"
              >
                <ImageIcon className="h-4 w-4" />
                {ocrLoading ? ocrStatus || '處理中…' : '從圖片辨識'}
              </button>
              <button
                type="button"
                onClick={generateTestParticipants}
                className="inline-flex items-center gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
              >
                <Sparkles className="h-4 w-4" />
                一鍵生成 {TEST_PARTICIPANT_COUNT} 個測試人員
              </button>
            </div>
          </>
        )}
      </section>

      {/* 區塊二：場地（可收合） */}
      <section className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <button
          type="button"
          onClick={() => setCourtListExpanded(v => !v)}
          className="mb-3 flex w-full items-center gap-2 text-left text-lg font-semibold text-gray-900 dark:text-gray-100"
          aria-expanded={courtListExpanded}
        >
          {courtListExpanded ? (
            <ChevronDown className="h-5 w-5 text-primary" />
          ) : (
            <ChevronRight className="h-5 w-5 text-primary" />
          )}
          <MapPin className="h-5 w-5 text-primary" />
          場地
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
            （{courts.length} 個）
          </span>
        </button>
        {courtListExpanded && (
          <>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              可增減場地並自訂編號或名稱。
            </p>
            <ul className="mb-3 space-y-2">
              {courts.map(c => (
                <li
                  key={c.id}
                  className="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700/50"
                >
                  <input
                    type="text"
                    value={c.label}
                    onChange={e => updateCourtLabel(c.id, e.target.value)}
                    placeholder="場地編號或名稱"
                    className="min-w-[120px] rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => removeCourt(c.id)}
                    className="rounded p-1.5 text-gray-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    aria-label="移除"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={addCourt}
              className="inline-flex items-center gap-2 rounded border border-dashed border-gray-400 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10 dark:hover:text-primary"
            >
              <Plus className="h-4 w-4" />
              新增場地
            </button>
          </>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          本工具用於羽球雙打編隊：先新增參加人員並設定等級（1～5），再新增場地與編號，選擇「等級平衡」可讓兩隊總實力接近，「隨機」則完全隨機分隊。產生後每場地會顯示一組
          2v2 對戰，括號內為等級方便辨識。人數需為 4 的倍數且不超過「場地數 ×
          4」。換場時僅從「未在場上」的人中編排，自動編排會讓上場次數少者優先以維持公平。晚到者：加入後從下一次換場起排隊（當時上場次數為
          0 會優先被排上），不補打、不補場次。
        </p>
      </section>

      {/* 換場對話框 */}
      {rotateDialogOpen &&
        rotateTargetMatchIndex !== null &&
        matches?.[rotateTargetMatchIndex] && (
          <RotateDialog
            match={matches[rotateTargetMatchIndex]}
            participantsNotInMatch={getParticipantsNotInAnyMatch()}
            onConfirm={(mode, specifySelected) =>
              applyRotate(mode, specifySelected)
            }
            onCancel={() => {
              setRotateDialogOpen(false);
              setRotateTargetMatchIndex(null);
            }}
          />
        )}
    </>
  );
}

/** 換場對話框：可選方式或指定人 */
const LEVEL_OPTIONS = [1, 2, 3, 4, 5] as const;
const SPECIFY_MAX = 4;

function RotateDialog({
  match,
  participantsNotInMatch,
  onConfirm,
  onCancel,
}: {
  match: Match;
  participantsNotInMatch: Participant[];
  onConfirm: (mode: RotateMode, specifySelected: Participant[] | null) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<RotateMode>('auto');
  const [levelFilter, setLevelFilter] = useState<number | ''>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const matchFour = [
    match.teamA[0],
    match.teamA[1],
    match.teamB[0],
    match.teamB[1],
  ];

  const byLevel =
    levelFilter === ''
      ? participantsNotInMatch
      : participantsNotInMatch.filter(p => p.level === levelFilter);

  const togglePerson = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < SPECIFY_MAX) next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (mode === 'specify') {
      const selected = participantsNotInMatch.filter(p =>
        selectedIds.has(p.id)
      );
      onConfirm('specify', selected.length >= 1 ? selected : null);
    } else {
      onConfirm('auto', null);
    }
  };

  const selectedCount = selectedIds.size;
  const restCount = participantsNotInMatch.length - selectedCount;
  const needFill = 4 - selectedCount;
  const canFill = needFill <= 0 || restCount >= needFill;
  const canConfirmSpecify =
    selectedCount >= 1 &&
    selectedCount <= SPECIFY_MAX &&
    (selectedCount === 4 || canFill);
  const canConfirm =
    (mode === 'auto' && participantsNotInMatch.length >= 4) ||
    (mode === 'specify' && canConfirmSpecify);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rotate-dialog-title"
    >
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <h2
          id="rotate-dialog-title"
          className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          換場
        </h2>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {match.courtLabel}：{matchFour.map(p => p.name).join('、')}
        </p>

        <div className="mb-4 space-y-2">
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="rotate-mode"
              checked={mode === 'auto'}
              onChange={() => setMode('auto')}
              className="mt-1.5 shrink-0 text-primary"
            />
            <span>
              <span className="text-gray-700 dark:text-gray-300">自動編排</span>
              <span className="ml-1 block text-xs text-gray-500 dark:text-gray-400">
                未在場上的人中，上場次數少者優先；不補打
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="rotate-mode"
              checked={mode === 'specify'}
              onChange={() => setMode('specify')}
              className="text-primary"
            />
            <span className="text-gray-700 dark:text-gray-300">指定編排</span>
          </label>
        </div>

        {mode === 'specify' && (
          <div className="mb-4 space-y-3">
            <div>
              <span className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                等級
              </span>
              <select
                value={levelFilter}
                onChange={e =>
                  setLevelFilter(
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
                className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">全部</option>
                {LEVEL_OPTIONS.map(l => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                人員（可選 1～4 人，不足由系統補齊）
              </span>
              <div className="max-h-40 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700/50">
                {byLevel.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {levelFilter === ''
                      ? '無未上場人員'
                      : `等級 ${levelFilter} 無未上場人員`}
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {byLevel.map(p => (
                      <li key={p.id}>
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={() => togglePerson(p.id)}
                            className="rounded text-primary"
                          />
                          <span className="text-gray-900 dark:text-gray-100">
                            {p.name}({p.level})
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {selectedCount > 0 && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  已選 {selectedCount} 人
                  {selectedCount < 4 && restCount >= 4 - selectedCount && (
                    <>，將自動補齊 {4 - selectedCount} 人</>
                  )}
                  {selectedCount < 4 && restCount < 4 - selectedCount && (
                    <>，未上場人數不足無法補齊</>
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="rounded bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            確定換場
          </button>
        </div>
      </div>
    </div>
  );
}
